import { ANY_PROFESSION_ID } from "constants/skuQualificationConstants";
import { VerificationStatusIds } from "types/common-types";
import type {
  AccreditationChainItem,
  Profession,
  SkuQualification,
  SkuQualificationView,
} from "services/KnowfullyAdminClient";

export const ALL_APPROVED_ID = "AllApprovedId";
export const MULTIPLE_VALUES_INDICATOR = "MULTIPLE_VALUES" as const;

type MultipleValuesIndicator = typeof MULTIPLE_VALUES_INDICATOR;

export type SkuQualificationGroupBy =
  | "licensureBody"
  | "accreditationBody"
  | "submissionAuthority";

// LB / AB / SA fields derived from an accreditation-chain item, flattened onto a row.
type AccreditationChainFields = {
  licensureBodyId?: string;
  licensureBodyName?: string;
  licensureBodyAcronym?: string;
  accreditationBodyId?: string;
  accreditationBodyName?: string;
  accreditationBodyAcronym?: string;
  submissionAuthorityId?: string;
  submissionAuthorityName?: string;
};

// One fanned row = one SkuQualification x one chain item. Keeps the real SkuQualification
// `id` so a delete resolves to a concrete entity even after fan-out.
export type SkuQualificationRow = SkuQualification &
  AccreditationChainFields & { isExpired: boolean };

export type SkuQualificationGroupModel =
  | (SkuQualificationRow & { isGroup: false })
  | (SkuQualificationRow & {
      isGroup: true;
      childQualifications: SkuQualificationRow[];
      memberCount: number;
    });

const allSame = <T,>(items: T[], accessor: keyof T): boolean => {
  if (items.length === 0) return true;
  const firstValue = items[0][accessor];
  return items.every((item) => {
    const value = item[accessor];
    if (value instanceof Date && firstValue instanceof Date) {
      return value.getTime() === firstValue.getTime();
    }
    return value === firstValue;
  });
};

export const getAggregatedValue = <T, K extends keyof T>(
  items: T[],
  accessor: K
): T[K] | MultipleValuesIndicator => {
  if (items.length === 0) return undefined as T[K];
  if (allSame(items, accessor)) {
    return items[0][accessor];
  }
  return MULTIPLE_VALUES_INDICATOR;
};

export const toKeyPart = (value: Date | string | number | undefined | null): string => {
  if (value === undefined || value === null) return "";
  if (value instanceof Date) return value.toISOString();
  return String(value);
};

const toTime = (value: Date | string | undefined | null): number | undefined => {
  if (value === undefined || value === null) return undefined;
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(time) ? undefined : time;
};

// Display-only: drives the expired chip, grey styling and valid/expired split.
// Actual visibility/filtering stays server-driven (the "From" filter).
const isRowExpired = (
  expiration: Date | string | undefined,
  referenceDate: Date
): boolean => {
  const time = toTime(expiration);
  return time !== undefined && time < referenceDate.getTime();
};

// Fan-out: expand each view into one row per chain item. An empty chain yields a
// single row with blank LB/SA (the ~74%-of-QA reality). AB falls back to the
// SkuQualification's own qualification when the chain omits it.
const fanOut = (
  views: SkuQualificationView[],
  referenceDate: Date
): SkuQualificationRow[] => {
  const rows: SkuQualificationRow[] = [];
  for (const view of views) {
    const chain = view.accreditationChain ?? [];
    const items: (AccreditationChainItem | undefined)[] = chain.length
      ? chain
      : [undefined];
    const expired = isRowExpired(view.creditSubmissionExpirationDate, referenceDate);
    for (const item of items) {
      rows.push({
        ...view,
        licensureBodyId: item?.licensureBodyId,
        licensureBodyName: item?.licensureBodyName,
        licensureBodyAcronym: item?.licensureBodyAcronym,
        accreditationBodyId: item?.accreditationBodyId ?? view.qualification?.id,
        accreditationBodyName: item?.accreditationBodyName ?? view.qualification?.value,
        accreditationBodyAcronym: item?.accreditationBodyAcronym,
        submissionAuthorityId: item?.submissionAuthorityId,
        submissionAuthorityName: item?.submissionAuthorityName,
        isExpired: expired,
      } as SkuQualificationRow);
    }
  }
  return rows;
};

const validityPart = (row: SkuQualificationRow): string =>
  row.isExpired ? "expired" : "valid";

// Full LB x AB x SA x Profession identity (+ validity). Expiration date, State, dates
// and credits are intentionally NOT part of the key.
const identityKey = (row: SkuQualificationRow): string =>
  [
    toKeyPart(row.licensureBodyId),
    toKeyPart(row.accreditationBodyId),
    toKeyPart(row.submissionAuthorityId),
    row.profession?.id || ANY_PROFESSION_ID,
    validityPart(row),
  ].join("_");

const groupKey = (
  row: SkuQualificationRow,
  groupBy: SkuQualificationGroupBy
): string => {
  switch (groupBy) {
    // By-AB and By-SA group by their dimension + Profession: professions are never mixed
    // (the course number can differ). Everything else (AB/LB/SA, course, dates, credits)
    // stays out of the key and rolls up.
    case "accreditationBody":
      return [
        toKeyPart(row.accreditationBodyId),
        row.profession?.id || ANY_PROFESSION_ID,
        validityPart(row),
      ].join("_");
    case "submissionAuthority":
      return [
        toKeyPart(row.submissionAuthorityId),
        row.profession?.id || ANY_PROFESSION_ID,
        validityPart(row),
      ].join("_");
    case "licensureBody":
    default:
      return identityKey(row);
  }
};

// Sku Validation rolls up worst-case-wins: a group takes its most severe member's status
// (Failure > Pending > Success > none), so a failing member is never hidden behind the
// first member's neutral chip. Returns the member itself so its status object + message
// stay coherent.
const VERIFICATION_SEVERITY: Record<string, number> = {
  [VerificationStatusIds.Failure]: 3,
  [VerificationStatusIds.Pending]: 2,
  [VerificationStatusIds.Success]: 1,
};
const verificationSeverity = (row: SkuQualificationRow): number =>
  VERIFICATION_SEVERITY[row.verificationStatus?.id ?? ""] ?? 0;
const worstVerificationMember = (
  members: SkuQualificationRow[]
): SkuQualificationRow =>
  members.reduce((worst, m) =>
    verificationSeverity(m) > verificationSeverity(worst) ? m : worst
  );

// Group row: rolls the identity fields that can disagree across members up to a chip
// (MULTIPLE_VALUES_INDICATOR) or resolves them to their single shared value.
const buildGroupRow = (
  members: SkuQualificationRow[],
  key: string
): SkuQualificationGroupModel => {
  const [first] = members;
  const worst = worstVerificationMember(members);
  const professionIds = new Set(
    members.map((m) => m.profession?.id || ANY_PROFESSION_ID)
  );
  const rolledProfession =
    professionIds.size === 1
      ? first.profession
      : ({ name: MULTIPLE_VALUES_INDICATOR } as Profession);

  return {
    ...first,
    id: `group_${key}`,
    isGroup: true,
    childQualifications: members,
    memberCount: members.length,
    licensureBodyId: getAggregatedValue(members, "licensureBodyId"),
    licensureBodyName: getAggregatedValue(members, "licensureBodyName"),
    licensureBodyAcronym: getAggregatedValue(members, "licensureBodyAcronym"),
    accreditationBodyId: getAggregatedValue(members, "accreditationBodyId"),
    accreditationBodyName: getAggregatedValue(members, "accreditationBodyName"),
    accreditationBodyAcronym: getAggregatedValue(members, "accreditationBodyAcronym"),
    submissionAuthorityId: getAggregatedValue(members, "submissionAuthorityId"),
    submissionAuthorityName: getAggregatedValue(members, "submissionAuthorityName"),
    courseNumber: getAggregatedValue(members, "courseNumber"),
    approvalCode: getAggregatedValue(members, "approvalCode"),
    approvalDate: getAggregatedValue(members, "approvalDate"),
    releaseDate: getAggregatedValue(members, "releaseDate"),
    creditSubmissionExpirationDate: getAggregatedValue(members, "creditSubmissionExpirationDate"),
    credits: getAggregatedValue(members, "credits"),
    minimumCredit: getAggregatedValue(members, "minimumCredit"),
    partialCreditIncrement: getAggregatedValue(members, "partialCreditIncrement"),
    notes: getAggregatedValue(members, "notes"),
    profession: rolledProfession,
    verificationStatus: worst.verificationStatus,
    verificationMessage: worst.verificationMessage,
  } as SkuQualificationGroupModel;
};

const str = (value: unknown): string => (value == null ? "" : String(value));

// AB-first ordering (accreditationBody / submissionAuthority views).
const byAccreditationBodyFirst = (
  a: SkuQualificationGroupModel,
  b: SkuQualificationGroupModel
): number =>
  str(a.accreditationBodyName).localeCompare(str(b.accreditationBodyName)) ||
  str(a.licensureBodyName).localeCompare(str(b.licensureBodyName)) ||
  str(a.submissionAuthorityName).localeCompare(str(b.submissionAuthorityName)) ||
  str(a.profession?.name).localeCompare(str(b.profession?.name));

// LB-first ordering (By-Licensure-Body view: "flat, ordered by Licensure Body").
const byLicensureBodyFirst = (
  a: SkuQualificationGroupModel,
  b: SkuQualificationGroupModel
): number =>
  str(a.licensureBodyName).localeCompare(str(b.licensureBodyName)) ||
  str(a.accreditationBodyName).localeCompare(str(b.accreditationBodyName)) ||
  str(a.submissionAuthorityName).localeCompare(str(b.submissionAuthorityName)) ||
  str(a.profession?.name).localeCompare(str(b.profession?.name));

// Expired always sorts last (unconditional), then the primary chain per `groupBy`.
const sortRows = (
  rows: SkuQualificationGroupModel[],
  groupBy: SkuQualificationGroupBy
): SkuQualificationGroupModel[] => {
  const compare =
    groupBy === "licensureBody" ? byLicensureBodyFirst : byAccreditationBodyFirst;
  return [...rows].sort((a, b) => {
    if (a.isExpired !== b.isExpired) return a.isExpired ? 1 : -1;
    return compare(a, b);
  });
};

// Fan out, then re-group per `groupBy`: single-member keys stay flat (isGroup:false),
// multi-member keys become expandable groups. `referenceDate` is injected so expiry is pure.
export const groupSkuQualifications = (
  qualifications: SkuQualificationView[],
  groupBy: SkuQualificationGroupBy = "licensureBody",
  referenceDate: Date = new Date()
): SkuQualificationGroupModel[] => {
  const fanned = fanOut(qualifications ?? [], referenceDate);

  const groups = new Map<string, SkuQualificationRow[]>();
  for (const row of fanned) {
    const key = groupKey(row, groupBy);
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }

  const result: SkuQualificationGroupModel[] = [];
  groups.forEach((members, key) => {
    if (members.length === 1) {
      result.push({ ...members[0], isGroup: false } as SkuQualificationGroupModel);
    } else {
      result.push(buildGroupRow(members, key));
    }
  });

  return sortRows(result, groupBy);
};

export const countLicensureBodies = (
  members: Pick<SkuQualificationRow, "licensureBodyId">[]
): number =>
  new Set(
    members
      .map((member) => member.licensureBodyId)
      .filter((id): id is string => !!id && id.trim() !== "")
  ).size;

// A group's own `id` is synthetic, so deleting a group fans out over its member rows' ids;
// a single row deletes by its own id.
export const resolveSkuQualificationDeleteIds = (
  target: SkuQualification | SkuQualificationGroupModel
): string[] => {
  if ("isGroup" in target && target.isGroup) {
    // The licensure body is derived, so several member rows share one record: one delete each.
    return Array.from(
      new Set(
        (target.childQualifications ?? [])
          .map((c) => c.id)
          .filter((id): id is string => !!id)
      )
    );
  }
  return target.id ? [target.id] : [];
};

export const processFormValues = (professionId: string | undefined, stateIds: string[] | undefined) => {
  let processedProfessionId = professionId;
  let processedStateIds = stateIds;
  let useQualificationApprovals = false;

  if (professionId === ANY_PROFESSION_ID) {
    processedProfessionId = undefined;
  } else if (professionId === ALL_APPROVED_ID) {
    processedProfessionId = undefined;
    useQualificationApprovals = true;
  }

  if (Array.isArray(stateIds) && stateIds.includes(ALL_APPROVED_ID)) {
    processedStateIds = undefined;
    useQualificationApprovals = true;
  }

  return { professionId: processedProfessionId, stateIds: processedStateIds, useQualificationApprovals };
};

// Structural on purpose: both the grouped and the pre-grouped row shapes flow through here.
export const hasGroupLockedFields = (
  qualification: {
    immutable?: boolean;
    isGroup?: boolean;
    childQualifications?: { immutable?: boolean }[];
  }
): boolean => {
  if ("isGroup" in qualification && qualification.isGroup === true) {
    return qualification.childQualifications?.some((c) => c.immutable) ?? false;
  }
  return qualification.immutable === true;
};
